import { useState } from "react";
import { useLang, ensureSessionId } from "@/context/LanguageContext";
import { api, API, trackEvent } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PreToolDisclaimer from "@/components/PreToolDisclaimer";

const QUESTIONS = [
  { key: "habitual_residence_status", opts: ["clear", "disputed", "changed_recently", "unclear"] },
  { key: "child_current_location_status", opts: ["same_as_habitual", "different", "unknown"] },
  { key: "hague_relationship", opts: ["possibly_applicable", "not_indicated", "unclear"] },
  { key: "removal_or_retention", opts: ["neither", "possible_removal", "possible_retention", "unclear"] },
  { key: "existing_custody_order", opts: ["yes_domestic", "yes_foreign", "no", "unclear"] },
  { key: "travel_authorization_status", opts: ["documented", "not_documented", "disputed", "na"] },
  { key: "pending_proceedings", opts: ["none", "one_jurisdiction", "multiple_jurisdictions", "unclear"] },
  { key: "safety_concerns", opts: ["no", "yes", "unclear"] },
  { key: "coercion_concerns", opts: ["no", "yes", "unclear"] },
  { key: "immigration_dependency", opts: ["no", "yes", "unclear"] },
  { key: "communication_between_parents", opts: ["functional", "strained", "hostile"] },
  { key: "evidence_preservation_status", opts: ["organized", "partial", "not_done", "unclear"] },
  { key: "urgency_level", opts: ["low", "medium", "high"] },
  { key: "existing_agreements", opts: ["yes", "no", "unclear"] },
  { key: "recognition_or_enforcement_abroad", opts: ["likely_needed", "not_indicated", "unclear"] },
];

export default function Assessment() {
  const { t, lang } = useLang();
  const [acknowledged, setAcknowledged] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);

  const canSubmit = QUESTIONS.every((q) => answers[q.key]);

  const submit = async () => {
    try {
      const r = await api.post("/assessment", { ...answers, language: lang, session_id: ensureSessionId() });
      setResult(r.data.result); setAssessmentId(r.data.id);
      trackEvent("framework_completed", { tool: "cross_border_assessment" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not generate the MIFT Assessment Map."); }
  };

  const downloadPdf = () => {
    if (!assessmentId) return;
    trackEvent("assessment_map_pdf_downloaded", { tool: "cross_border_assessment" });
    window.open(`${API}/assessment/${assessmentId}/pdf`, "_blank");
  };

  const Section = ({ title, items }) => items && items.length > 0 ? (
    <div className="mt-6">
      <h3 className="font-semibold text-slate-100 text-sm uppercase tracking-wider text-blue-300">{title}</h3>
      <ul className="mt-3 space-y-2 text-sm text-slate-300">
        {items.map((n, i) => <li key={i} className="flex gap-2"><span className="text-blue-400 flex-shrink-0">→</span> {n}</li>)}
      </ul>
    </div>
  ) : null;

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20" data-testid="assessment-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.assessment.title}</h1>
      <p className="mt-4 text-slate-300 max-w-3xl">{t.assessment.sub}</p>

      <PreToolDisclaimer showHague />

      {/* Privacy Notice */}
      <div className="mt-8 border border-amber-500/30 bg-amber-500/5 rounded-lg p-5" data-testid="assessment-privacy-notice">
        <div className="flex items-start gap-2 text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-sm">{t.assessment.privacy_notice_title}</div>
            <p className="mt-1 text-sm text-amber-100/80 leading-relaxed">{t.assessment.privacy_notice_body}</p>
          </div>
        </div>
        {!result && (
          <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
                   data-testid="assessment-acknowledge" className="accent-blue-500" />
            <span>{t.assessment.privacy_notice_title} — acknowledged</span>
          </label>
        )}
      </div>

      {result ? (
        <div className="mt-10 card-mift p-6" data-testid="assessment-result">
          <h2 className="text-2xl font-semibold text-blue-300">{t.assessment.result_title}</h2>
          <p className="mt-3 text-sm text-slate-400 italic">{t.assessment.framing}</p>
          <Section title={t.assessment.section_issues} items={result.issues} />
          <Section title={t.assessment.section_info} items={result.info_needed} />
          <Section title={t.assessment.section_safety} items={result.safety} />
          <Section title={t.assessment.section_procedural} items={result.procedural} />
          <Section title={t.assessment.section_cross} items={result.cross_border} />
          <Section title={t.assessment.section_questions} items={result.questions} />
          <p className="mt-8 text-xs text-slate-500 italic">{result.disclaimer}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button className="btn-primary inline-flex items-center gap-2" onClick={downloadPdf} data-testid="assessment-download-pdf">
              <Download className="w-4 h-4" /> {t.assessment.download_pdf}
            </button>
            <button className="btn-ghost" onClick={() => { setResult(null); setAnswers({}); setAssessmentId(null); }} data-testid="assessment-restart">
              {t.assessment.restart}
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">{t.assessment.pdf_note}</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-5">
          {QUESTIONS.map((q, i) => (
            <div key={q.key} className="card-mift p-5" data-testid={`q-${q.key}`}>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Q{i + 1}</div>
              <div className="mt-1 text-slate-100 font-medium">{t.assessment.q[q.key]}</div>
              <div className="mt-4 max-w-md">
                <Select value={answers[q.key] || ""} onValueChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))}>
                  <SelectTrigger data-testid={`select-${q.key}`} className="bg-[#0A1224] border-slate-700"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1224] border-slate-700 text-slate-100">
                    {q.opts.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`opt-${q.key}-${o}`}>{t.assessment.opts[o]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          <button onClick={submit} disabled={!canSubmit || !acknowledged}
                  data-testid="assessment-submit"
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed w-max">
            {t.assessment.submit}
          </button>
        </div>
      )}
    </div>
  );
}
